import {
  classifyFile,
  scaleToLongEdge,
} from '@/domain/menu-import/capture-files';
import { analyzePixels } from '@/domain/menu-import/photo-checks';
import type { IPixelAnalysis } from '@/domain/menu-import/photo-checks';
import {
  isTallPage,
  PART_MAX_WIDTH,
  PART_MIN_WIDTH,
} from '@/domain/menu-import/tall-pages';
import {
  canvasToJpeg,
  context2d,
  createCanvas,
  releaseCanvas,
} from './capture-dom';
import { heicToJpeg } from './heic';
import { splitTallPage } from './tall-parts';
import type { ITallPart, ITallSplitResult } from './tall-parts';

export const PHOTO_JPEG_QUALITY = 0.85;
/** Long edge of the small copy measured by the photo checks. */
export const ANALYSIS_LONG_EDGE = 400;

export interface IProcessedImage {
  /** JPEG, white behind transparent pixels. */
  blob: Blob;
  width: number;
  height: number;
  /** Short side of the JPEG, in pixels (what the server and the photo checks see). */
  shortSide: number;
  analysis: IPixelAnalysis;
}

/** The file is not an image this browser (or the HEIC decoder) can read. */
export class ImageUnreadableError extends Error {
  constructor() {
    super('IMAGE_UNREADABLE');
    this.name = 'ImageUnreadableError';
  }
}

/** A decoded image ready to draw (also used by the store logo, `src/lib/store-setup/logo-image.ts`). */
export interface IDecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  /** Frees the decoded pixels (bitmap closed, object URL revoked); call after drawing. */
  release(): void;
}

async function decodeWithBitmap(blob: Blob): Promise<IDecodedImage> {
  const bitmap = await createImageBitmap(blob, {
    imageOrientation: 'from-image',
  });
  return {
    source: bitmap,
    width: bitmap.width,
    height: bitmap.height,
    release: () => bitmap.close(),
  };
}

function loadImage(image: HTMLImageElement, url: string): Promise<void> {
  if (typeof image.decode === 'function') {
    image.src = url;
    return image.decode();
  }
  return new Promise((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new ImageUnreadableError());
    image.src = url;
  });
}

/** Safari < 15 has no `createImageBitmap`: an `<img>` on an object URL, revoked after drawing. */
async function decodeWithImage(blob: Blob): Promise<IDecodedImage> {
  const url = URL.createObjectURL(blob);
  const image = new Image();
  try {
    await loadImage(image, url);
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
  return {
    source: image,
    width: image.naturalWidth,
    height: image.naturalHeight,
    release: () => URL.revokeObjectURL(url),
  };
}

/** `createImageBitmap` (EXIF orientation applied) when it works, else an `<img>`. */
export async function decodeBlob(blob: Blob): Promise<IDecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await decodeWithBitmap(blob);
    } catch {
      // Engines before the `from-image` orientation option reject the call:
      // the <img> path below still decodes the photo.
    }
  }
  return decodeWithImage(blob);
}

/** Native decoding first; a HEIC/HEIF photo the browser cannot decode goes through `heic-to`. */
async function decodeFile(file: File): Promise<IDecodedImage> {
  try {
    return await decodeBlob(file);
  } catch {
    if (classifyFile(file) !== 'heic') throw new ImageUnreadableError();
  }
  try {
    return await decodeBlob(await heicToJpeg(file));
  } catch {
    throw new ImageUnreadableError();
  }
}

/** The brightness and sharpness of a 400 px copy of `canvas`, for the tray warnings. */
function analyzeCanvas(canvas: HTMLCanvasElement): IPixelAnalysis {
  const small = scaleToLongEdge(
    canvas.width,
    canvas.height,
    ANALYSIS_LONG_EDGE
  );
  let analysisCanvas: HTMLCanvasElement | null = null;
  try {
    analysisCanvas = createCanvas(small.width, small.height);
    const context = context2d(analysisCanvas);
    context.drawImage(canvas, 0, 0, small.width, small.height);
    const pixels = context.getImageData(0, 0, small.width, small.height);
    return analyzePixels(pixels.data, small.width, small.height);
  } finally {
    releaseCanvas(analysisCanvas);
  }
}

/**
 * A photo ready to upload (plan §10.2): EXIF orientation applied, scaled to
 * `maxLongEdge` (never upscaled), JPEG q0.85, plus the brightness and
 * sharpness of a 400 px copy for the tray warnings. Every canvas and decoded
 * image is released before returning.
 */
export async function processImage(
  file: File,
  maxLongEdge: number
): Promise<IProcessedImage> {
  return processDecoded(await decodeFile(file), maxLongEdge);
}

/** `processImage` of an already decoded image (released here). */
async function processDecoded(
  decoded: IDecodedImage,
  maxLongEdge: number
): Promise<IProcessedImage> {
  const { width, height } = scaleToLongEdge(
    decoded.width,
    decoded.height,
    maxLongEdge
  );
  let canvas: HTMLCanvasElement | null = null;

  try {
    try {
      canvas = createCanvas(width, height);
      const context = context2d(canvas);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.drawImage(decoded.source, 0, 0, width, height);
    } finally {
      decoded.release();
    }

    const blob = await canvasToJpeg(canvas, PHOTO_JPEG_QUALITY);
    return {
      blob,
      width,
      height,
      shortSide: Math.min(width, height),
      analysis: analyzeCanvas(canvas),
    };
  } finally {
    releaseCanvas(canvas);
  }
}

/**
 * One part of a very tall photo (a long screenshot), ready to upload. No
 * photo checks: like a PDF page it is a digital page, where a dark theme or
 * a plain background is not a bad photo, and it cannot be taken again.
 */
export type IProcessedPart = ITallPart;

/** A menu photo: one image, or the parts of a very tall one. */
export type TCapturePhoto =
  | { kind: 'image'; image: IProcessedImage }
  | { kind: 'parts'; result: ITallSplitResult };

/**
 * A photo of the menu capture: `processImage`, except that a very tall one
 * (a long screenshot, see `tall-pages.ts`) is split into parts at most
 * PART_MAX_WIDTH wide (a narrow one is drawn PART_MIN_WIDTH wide, the
 * server's minimum with a margin), each handed to `onPart` before the next
 * is drawn; at most `maxParts`.
 */
export async function processCapturePhoto(
  file: File,
  maxLongEdge: number,
  onPart: (part: IProcessedPart) => Promise<void>,
  maxParts: number
): Promise<TCapturePhoto> {
  const decoded = await decodeFile(file);
  if (!isTallPage(decoded.width, decoded.height)) {
    return { kind: 'image', image: await processDecoded(decoded, maxLongEdge) };
  }
  try {
    const scale = Math.max(
      Math.min(1, PART_MAX_WIDTH / decoded.width),
      PART_MIN_WIDTH / decoded.width
    );
    const result = await splitTallPage(
      {
        width: Math.max(1, Math.round(decoded.width * scale)),
        height: Math.max(1, Math.round(decoded.height * scale)),
        drawWindow: async (canvas, top) => {
          const context = context2d(canvas);
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(
            decoded.source,
            0,
            top / scale,
            decoded.width,
            canvas.height / scale,
            0,
            0,
            canvas.width,
            canvas.height
          );
        },
      },
      PHOTO_JPEG_QUALITY,
      onPart,
      maxParts
    );
    return { kind: 'parts', result };
  } finally {
    decoded.release();
  }
}
